import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  ImportExport,
  Search,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./PayrollTable.css";

const emptyForm = {
  id: "",
  employeeName: "",
  esicNumber: "",
  uanNumber: "",
  bankName: "",
  ifscCode: "",
  bankAccount: "",
  workingDays: "",
  lop: "",
  payoutStatus: "",
  gross: "",
  basic: "",
  hra: "",
  professionalFee: "",
  incentive: "",
  pfEmployer: "",
  pfEmployee: "",
  esiEmployer: "",
  esiEmployee: "",
  specialAllowance: "",
  pt: "",
  incomeTax: "",
  conveyanceAllowance: "",
  foodCoupons: "",
  groupInsurance: "",
  gratuity: "",
  internetAllowance: "",
  leaveTravelAllowance: "",
  phoneReimbursement: "",
  phoneAllowance: "",
  onsiteAllowance: "",
  salaryAdvDeduction: "",
  specialAllowance2: "",
  netPay: "",
  payrollStatus: "",
};

const PayrollTable = () => {
  const [data, setData] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [mode, setMode] = useState("add");
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [search, setSearch] = useState("");
  const fileInputRef = useRef(null);

  /* ---------------- MODAL ---------------- */
  const openModal = (type, row = null, index = null) => {
    setMode(type);
    setForm(row || emptyForm);
    setEditIndex(index);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setForm(emptyForm);
    setEditIndex(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (!Object.values(form).every((v) => v !== "")) {
      alert("All fields are required");
      return;
    }

    if (mode === "add") setData([...data, form]);
    if (mode === "edit") {
      const updated = [...data];
      updated[editIndex] = form;
      setData(updated);
    }
    closeModal();
  };

  const handleDelete = (index) => {
    if (window.confirm("Delete payroll record?")) {
      setData(data.filter((_, i) => i !== index));
    }
  };

  /* ---------------- SEARCH ---------------- */
  const filteredData = data.filter((row) =>
    Object.values(row).some((v) =>
      v.toString().toLowerCase().includes(search.toLowerCase())
    )
  );

  /* ---------------- EXPORT ---------------- */
  const handleExport = () => {
    if (!data.length) return alert("No data to export");

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payroll");

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "salary-payroll.xlsx"
    );
  };

  /* ---------------- IMPORT ---------------- */
  const handleImportClick = () => fileInputRef.current.click();

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);
      setData(json);
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="salary-finance-wrapper">
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept=".xlsx,.xls,.csv"
        onChange={handleImport}
      />

      <div className="salary-finance-card">
        {/* HEADER */}
        <div className="salary-finance-header">
          <h2>Salary & Payroll Management</h2>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search payroll..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search /> }}
            />

            <Button
              className="salary-btn"
              startIcon={<ImportExport />}
              onClick={handleImportClick}
            >
              Import
            </Button>

            <Button
              className="salary-btn"
              startIcon={<ImportExport />}
              onClick={handleExport}
            >
              Export
            </Button>

            <Button
              className="salary-btn add"
              startIcon={<Add />}
              onClick={() => openModal("add")}
            >
              Add Payroll
            </Button>
          </div>
        </div>

        {/* TABLE */}
        <TableContainer component={Paper} className="salary-finance-table">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Gross</TableCell>
                <TableCell>Net Pay</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.employeeName}</TableCell>
                  <TableCell>₹ {row.gross}</TableCell>
                  <TableCell className="net-pay">
                    ₹ {row.netPay}
                  </TableCell>
                  <TableCell>{row.payrollStatus}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => openModal("view", row)}>
                      <Visibility />
                    </IconButton>
                    <IconButton
                      color="warning"
                      onClick={() => openModal("edit", row, index)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(index)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {!filteredData.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No payroll records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* MODAL */}
      <Modal open={open} onClose={closeModal}>
        <Box className="salary-modal">
          <div className="modal-header">
            <h3>{mode.toUpperCase()} PAYROLL</h3>
            <IconButton onClick={closeModal}>
              <Close />
            </IconButton>
          </div>

          <div className="modal-grid">
            {Object.keys(emptyForm).map((key) => (
              <TextField
                key={key}
                name={key}
                label={key.replace(/([A-Z])/g, " $1")}
                value={form[key]}
                onChange={handleChange}
                disabled={mode === "view"}
                size="small"
                required
              />
            ))}
          </div>

          {mode !== "view" && (
            <div className="modal-footer">
              <Button className="salary-btn save" onClick={handleSave}>
                Save Payroll
              </Button>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default PayrollTable;
